"use client";

import type { ShareEntityType } from "@/hooks/use-chat";
import { captureClientEvent } from "@/lib/analytics/client";
import {
  ANALYTICS_EVENTS,
  type AnalyticsEventName,
} from "@/lib/analytics/events";

/**
 * One place that turns a share of *some entity* into the right named event.
 *
 * Sharing is the app's main organic growth loop, and it was completely
 * unmeasured: `event_shared`, `hiive_shared`, `venue_shared`, `post_shared`
 * and `profile_shared` were all declared and none were ever fired. Mapping
 * here rather than at each call site means a new shareable entity type gets
 * an event by adding one line, instead of being silently unmeasured.
 */
const EVENT_BY_ENTITY: Partial<Record<ShareEntityType, AnalyticsEventName>> = {
  event: ANALYTICS_EVENTS.EVENT_SHARED,
  hiive: ANALYTICS_EVENTS.HIIVE_SHARED,
  venue: ANALYTICS_EVENTS.VENUE_SHARED,
  post: ANALYTICS_EVENTS.POST_SHARED,
  profile: ANALYTICS_EVENTS.PROFILE_SHARED,
  story: ANALYTICS_EVENTS.STORY_SHARE_CLICKED,
};

export function trackEntityShare(
  entityType: ShareEntityType,
  entityId: string,
  properties?: Record<string, unknown>,
) {
  const event = EVENT_BY_ENTITY[entityType];

  captureClientEvent(event ?? ANALYTICS_EVENTS.CHAT_SHARE_SENT, {
    entity_type: entityType,
    entity_id: entityId,
    ...properties,
  });
}
